import React from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { flushSync } from "react-dom";
import PropTypes from "prop-types";
import { Accordion } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { formatMenuTitle } from "utils/menuUtils";
import { resetAppraisalSetupFlow } from "actions";

const MENU_ROUTE_ALIASES = {
  monthlyappraisals: "appraisal",
  quarterlyappraisals: "quarterly-appraisal",
  yearlyappraisals: "yearly-appraisals",
};

const VIEW_ROUTE_ALIASES = {
  "/monthlyappraisalview": ["/appraisal", "/monthlyappraisals"],
  "/monthlyappraisalreport": ["/monthly-reports"],
  "/appraisalprogressform": ["/appraisal"],
  "/quarterly-appraisal/view": ["/quarterly-appraisal"],
  "/quarterly-appraisal/paf/view": ["/quarterly-appraisal"],
  "/quarterlyappraisalview": ["/quarterly-appraisal", "/quarterlyappraisals"],
  "/yearly-appraisal/view": ["/yearly-appraisals"],
  "/yearly-appraisals/view": ["/yearly-appraisals"],
  "/yearly-appraisals/paf/view": ["/yearly-appraisals"],
  "/yearlyappraisalview": ["/yearly-appraisals"],
  "/probationary-appraisals/view": [
    "/probationary-appraisals",
    "/probational-appraisals",
  ],
  "/probational-appraisals/view": [
    "/probationary-appraisals",
    "/probational-appraisals",
  ],
};

const isAppraisalSetupPath = (pathname) =>
  pathname === "/appraisalSetup" ||
  pathname.startsWith("/appraisalSetup/") ||
  pathname === "/setupCategory";

const resolveMenuHref = (href) => {
  const normalized = href ? String(href).replace(/^\//, "") : "";
  if (!normalized) return "";
  return MENU_ROUTE_ALIASES[normalized] || normalized;
};

const isMenuItemActive = (pathname, menuHref, fromPath) => {
  if (!menuHref) return false;
  const target = `/${menuHref}`.replace(/\/+/g, "/");

  if (pathname === target || pathname.startsWith(`${target}/`)) {
    return true;
  }

  if (
    (pathname === "/appraisalprogressform" ||
      pathname.startsWith("/appraisalprogressform/")) &&
    fromPath
  ) {
    const source = `/${String(fromPath).replace(/^\//, "")}`;
    return source === target || source.startsWith(`${target}/`);
  }

  return Object.entries(VIEW_ROUTE_ALIASES).some(([viewRoute, listRoutes]) => {
    if (pathname === viewRoute || pathname.startsWith(`${viewRoute}/`)) {
      return listRoutes.includes(target);
    }
    return false;
  });
};

const NavigationListItem = ({
  title,
  href,
  depth,
  children,
  pageid,
  menuIcon,
  isMenuCollapse,
  setActiveKey,
  open,
  activeKey,
  onSelect,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const displayTitle = formatMenuTitle(title);
  const resolvedMenuHref = resolveMenuHref(href);
  const targetPath = resolvedMenuHref ? `/${resolvedMenuHref}` : "";
  const isActive = isMenuItemActive(
    location.pathname,
    resolvedMenuHref,
    location.state?.from
  );

  let paddingLeft = 8;
  if (depth > 0) {
    paddingLeft = 22 + 8 * depth;
  }

  const handleNavClick = (event) => {
    if (!targetPath || targetPath === "#") {
      event.preventDefault();
      return;
    }

    const leavingSetup = isAppraisalSetupPath(location.pathname);

    // Force synchronous route commit so history and React Router stay in sync
    // when leaving Appraisal Setup (async transitions were leaving the form stuck).
    event.preventDefault();
    flushSync(() => {
      navigate(targetPath);
    });

    if (leavingSetup) {
      dispatch(resetAppraisalSetupFlow());
    }
  };

  if (children) {
    const isExpanded = activeKey.includes(pageid);

    return (
      <Accordion.Item eventKey={pageid}>
        <Accordion.Header onClick={() => onSelect(pageid)}>
          {menuIcon && (
            <img
              src={menuIcon}
              alt=""
              aria-hidden="true"
              className={`nav-menu-icon me-2 ${isExpanded ? "expanded-icon" : ""}`}
            />
          )}
          <span>{displayTitle}</span>
        </Accordion.Header>
        <Accordion.Body className="submenu">{children}</Accordion.Body>
      </Accordion.Item>
    );
  }

  return (
    <div style={{ paddingLeft }}>
      <NavLink
        to={targetPath || "#"}
        onClick={handleNavClick}
        className={() => {
          if (depth === 0) {
            return [
              "nav-menu-link text-decoration-none mb-3",
              isActive ? "activeTab-sideNav" : "InactiveTab-sideNav",
            ].join(" ");
          }

          return [
            "nav-menu-link text-decoration-none submenu-item mb-3 border-0 bg-transparent",
            isActive ? "active" : "",
          ]
            .filter(Boolean)
            .join(" ");
        }}
        style={{
          textAlign: "left",
          display: depth === 0 ? "inline-flex" : "block",
        }}
      >
        {menuIcon && (
          <img
            src={menuIcon}
            alt=""
            aria-hidden="true"
            className="nav-menu-icon me-2"
          />
        )}
        {displayTitle}
      </NavLink>
    </div>
  );
};

NavigationListItem.propTypes = {
  children: PropTypes.node,
  depth: PropTypes.number.isRequired,
  href: PropTypes.string,
  open: PropTypes.bool,
  title: PropTypes.string.isRequired,
  pageid: PropTypes.string,
  menuIcon: PropTypes.string,
  isMenuCollapse: PropTypes.bool,
  setActiveKey: PropTypes.func,
  activeKey: PropTypes.array,
  onSelect: PropTypes.func,
};

NavigationListItem.defaultProps = {
  depth: 0,
  open: false,
};

export default NavigationListItem;
